import { useEffect, useRef } from 'react'

import useActionsReducer from '@slergberg/react-hook-use-actions-reducer'

const playerActions = {
  setDeck(state, payload) {
    const {
      disabled: deckDisabled = state.deckDisabled,
      slides: deckSlides = state.deckSlides,
      status: deckStatus = state.deckStatus,
      statusProgress: deckStatusProgress = state.deckStatusProgress,
    } = payload

    return {
      ...state,
      deckDisabled,
      deckSlides,
      deckStatus,
      deckStatusProgress,
    }
  },
  setIsDisabled(state, payload) {
    return { ...state, isDisabled: payload }
  },
  setRecordingMediaElementRef(state, payload) {
    return { ...state, recordingMediaElementRef: payload }
  },
  setPosition(state, payload) {
    const {
      recordingMediaElementRef: { current: recordingMediaElement } = {},
    } = state

    if (recordingMediaElement) {
      recordingMediaElement.currentTime = payload
    }

    return { ...state, position: payload, seekingPosition: payload }
  },
  setRecording(state, payload) {
    const {
      deckTimestamps: recordingDeckTimestamps = state.recordingDeckTimestamps,
      disabled: recordingDisabled = state.recordingDisabled,
      media: recordingMedia = state.recordingMedia,
      mediaFormat: recordingMediaFormat = state.recordingMediaFormat,
      status: recordingStatus = state.recordingStatus,
      statusProgress: recordingStatusProgress = state.recordingStatusProgress,
    } = payload

    return {
      ...state,
      recordingDeckTimestamps,
      recordingDisabled,
      recordingMedia,
      recordingMediaFormat,
      recordingStatus,
      recordingStatusProgress,
    }
  },
  setSeekingPosition(state, payload) {
    return { ...state, seekingPosition: payload }
  },
  onDurationChange(state, payload) {
    return { ...state, duration: payload }
  },
  onPositionChange(state, payload, dispatch) {
    dispatch({ type: 'updateDeckSlide' })

    return { ...state, position: payload, seekingPosition: payload }
  },
  play(state) {
    const {
      recordingMediaElementRef: { current: recordingMediaElement } = {},
    } = state

    if (recordingMediaElement) {
      recordingMediaElement.play()
    }

    return { ...state, isPlaying: true, isPaused: false, isStopped: false }
  },
  pause(state) {
    const {
      recordingMediaElementRef: { current: recordingMediaElement } = {},
    } = state

    if (recordingMediaElement) {
      recordingMediaElement.pause()
    }

    return { ...state, isPlaying: false, isPaused: true, isStopped: false }
  },
  resume(state) {
    const {
      recordingMediaElementRef: { current: recordingMediaElement } = {},
    } = state

    if (recordingMediaElement) {
      recordingMediaElement.play()
    }

    return { ...state, isPlaying: true, isPaused: false, isStopped: false }
  },
  stop(state) {
    const {
      recordingMediaElementRef: { current: recordingMediaElement } = {},
    } = state

    if (recordingMediaElement) {
      recordingMediaElement.pause()
      recordingMediaElement.currentTime = 0
    }

    return { ...state, isPlaying: false, isPaused: false, isStopped: true }
  },
  updateDeckSlide(state) {
    const timestampsUpToPosition = Object.keys(state.recordingDeckTimestamps)
      .map(Number)
      .filter((key) => Number(key) < state.position)
      .sort((a, b) => a - b)

    let { currentDeckSlide } = state
    if (timestampsUpToPosition.length <= 0) {
      currentDeckSlide = 0
    } else {
      currentDeckSlide =
        state.recordingDeckTimestamps[
          timestampsUpToPosition[timestampsUpToPosition.length - 1]
        ]
    }

    return { ...state, currentDeckSlide }
  },
}

const playerInitialState = {
  currentDeckSlide: 0,
  deckDisabled: false,
  deckSlides: [],
  deckStatus: null,
  deckStatusProgress: null,
  duration: 0,
  isDisabled: false,
  isPaused: false,
  isPlaying: false,
  isStopped: true,
  position: 0,
  recordingDeckTimestamps: {},
  recordingDisabled: false,
  recordingMedia: null,
  recordingMediaElementRef: null,
  recordingMediaFormat: null,
  recordingStatus: null,
  recordingStatusProgress: null,
  seekingPosition: 0,
}

export default function usePlayer(initialState = {}) {
  const recordingMediaElementRef = useRef()

  const isReducerActiveRef = useRef(true)
  useEffect(() => {
    isReducerActiveRef.current = true
    return () => {
      isReducerActiveRef.current = false
    }
  }, [isReducerActiveRef])

  const actionsReducerInitialStateRef = useRef({
    ...playerInitialState,
    ...initialState,
    isReducerActiveRef,
    recordingMediaElementRef,
  })
  useEffect(() => {
    actionsReducerInitialStateRef.current = {
      ...playerInitialState,
      ...initialState,
      isReducerActiveRef,
      recordingMediaElementRef,
    }
  }, [
    actionsReducerInitialStateRef,
    initialState,
    isReducerActiveRef,
    recordingMediaElementRef,
  ])

  const [player] = useActionsReducer(
    playerActions,
    actionsReducerInitialStateRef.current,
  )

  return player
}
